import React, { useLayoutEffect } from 'react';
import { getSelection } from '../../../../../utils/domUtil';
import { getClosestDomElement } from '../../../../../utils/dom/domTraversalUtils';

const SCROLL_PADDING = 50;

/**
 * This hook adds in a resize observer on the scrollable list, which listens for a height decrease (mainly due to
 * virtual keyboard showing) and adjusts the scroll position to show the focused element in the viewport if needed.
 *
 * This is important as virtual keyboard can cover the focused element on tablets, and cause
 * the virtual keyboard to close immediately after it shows.
 */
export default (scrollableListRef: React.RefObject<HTMLDivElement>) => {
    const lastScrollableListHeightRef = React.useRef<number | undefined>();

    useLayoutEffect(() => {
        if (!scrollableListRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries.length === 0) return;
            if (!scrollableListRef.current) return;

            // We only want to change scroll positioning if the height of the scrollable list has decreased
            const newHeight = entries[0].contentRect.height;
            if (lastScrollableListHeightRef.current && newHeight > lastScrollableListHeightRef.current) return;

            lastScrollableListHeightRef.current = newHeight;

            const focusNode = getSelection()?.focusNode;
            const focusElement = getClosestDomElement(focusNode);
            if (!focusElement) return;

            const isSelectionInPopup = scrollableListRef.current.contains(focusElement);
            if (!isSelectionInPopup) return;

            const focusElementRect = focusElement.getBoundingClientRect();
            const scrollableListRect = scrollableListRef.current.getBoundingClientRect();

            const isFocusContainedInScrollableList =
                focusElementRect.top > scrollableListRect.top + SCROLL_PADDING &&
                focusElementRect.bottom < scrollableListRect.bottom - SCROLL_PADDING;
            if (isFocusContainedInScrollableList) return;

            scrollableListRef.current.scrollTop =
                scrollableListRef.current.scrollTop + focusElementRect.top - scrollableListRect.top - SCROLL_PADDING;
        });

        resizeObserver.observe(scrollableListRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);
};
